import { OrganizationAddress, OrganizationLocalAddress } from '@/features/organization/api/OrganizationModel'
import {
  Alert,
  CardBody,
  InputGroup,
  useAddressLatinCharacterValidation
} from '@/common'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactCountryFlag from 'react-country-flag'

import { NON_EU_COUNTRIES } from '@/common'

function AddressForm({
  isLocal = true,
  street,
  streetNumber,
  street2,
  postCode,
  poBox,
  city,
  subDivision,
  country,
  onInputChange
}: {
  isLocal?: boolean
  street?: string
  streetNumber?: string
  street2?: string
  postCode?: string
  poBox?: string
  city: string
  subDivision?: string
  country: string
  onInputChange: (
    evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>
  ) => void
}) {
  const { t } = useTranslation()

  // Use generalized validation hook for English fields only
  const errors = useAddressLatinCharacterValidation(
    isLocal
      ? {}
      : {
          street,
          street2: street2 || '',
          streetNumber,
          postCode: postCode || '',
          poBox,
          city,
          subDivision
        },
    t('common:errors.latinCharacters')
  )

  return (
    <div>
      <p className="text-sm mb-1.5">
        {t('settings:organizationDetails.details.address')}{' '}
        <ReactCountryFlag countryCode={isLocal ? country : 'gb'} svg />
      </p>
      <div className="space-y-4 bg-background-main p-4">
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            id="street"
            label={t('common:inputFields.street.label')}
            value={street}
            onChange={onInputChange}
            error={!isLocal ? errors.street || undefined : undefined}
          />

          <InputGroup
            id="streetNumber"
            label={t('common:inputFields.streetNumber.label')}
            value={streetNumber}
            onChange={onInputChange}
            error={!isLocal ? errors.streetNumber || undefined : undefined}
          />
        </div>

        <InputGroup
          id="street2"
          label={t('common:inputFields.street2.label')}
          value={street2}
          onChange={onInputChange}
          error={!isLocal ? errors.street2 || undefined : undefined}
        />

        <div className="grid md:grid-cols-3 gap-4">
          <InputGroup
            id="postCode"
            label={t('common:inputFields.postCode.label')}
            value={postCode}
            onChange={onInputChange}
            required={
              NON_EU_COUNTRIES.find(nonEuCountry => nonEuCountry.value === country)
                ?.isPostCodeRequired
            }
            error={!isLocal ? errors.postCode || undefined : undefined}
          />
          <InputGroup
            id="city"
            label={t('common:inputFields.city.label')}
            value={city}
            onChange={onInputChange}
            required
            error={!isLocal ? errors.city || undefined : undefined}
          />
          <InputGroup
            id="poBox"
            label={t('common:inputFields.poBox.label')}
            value={poBox}
            onChange={onInputChange}
            error={!isLocal ? errors.poBox || undefined : undefined}
          />
        </div>
        <InputGroup
          id="subDivision"
          label={t('common:inputFields.subDivision.label')}
          value={subDivision}
          onChange={onInputChange}
          error={!isLocal ? errors.subDivision || undefined : undefined}
        />
      </div>
    </div>
  )
}

export default function OrganizationWithLocalAddressForm({
  initialState,
  onChange
}: {
  initialState: OrganizationAddress & OrganizationLocalAddress
  onChange: (address: OrganizationAddress & OrganizationLocalAddress) => void
}) {
  const { t } = useTranslation()
  const [state, setState] = useState(initialState)
  const {
    street,
    streetNumber,
    subDivision,
    country,
    postCode,
    poBox,
    city,
    street2,
    localStreet,
    localStreetNumber,
    localSubDivision,
    localPostCode,
    localPoBox,
    localCity,
    localStreet2
  } = state

  const countryName = t(`countriesByIsoAlpha2:${country}`)

  const onInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      const id = evt.target.id
      setState({ ...state, [id]: evt.target.value })
      onChange({ ...state, [id]: evt.target.value })
    },
    [state, onChange]
  )

  const onLocalInputChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
      const id = evt.target.id
      setState({ ...state, [`local${id.charAt(0).toUpperCase() + id.slice(1)}`]: evt.target.value })
      onChange({
        ...state,
        [`local${id.charAt(0).toUpperCase() + id.slice(1)}`]: evt.target.value
      })
    },
    [state, onChange]
  )

  return (
    <CardBody className="space-y-4">
      <Alert type="info">{t('settings:organizationDetails.details.localAddressInfo')}</Alert>
      <div className="grid grid-cols-2 gap-4">
        <AddressForm
          street={localStreet}
          streetNumber={localStreetNumber}
          street2={localStreet2}
          postCode={localPostCode}
          country={country}
          poBox={localPoBox}
          city={localCity}
          subDivision={localSubDivision}
          onInputChange={onLocalInputChange}
        />
        <AddressForm
          isLocal={false}
          street={street}
          streetNumber={streetNumber}
          street2={street2}
          postCode={postCode}
          poBox={poBox}
          city={city}
          subDivision={subDivision}
          country={country}
          onInputChange={onInputChange}
        />
      </div>
      <div className="w-3/5">
        <InputGroup
          id="country"
          label={t('common:inputFields.country.label')}
          value={countryName}
          disabled
        />
      </div>
    </CardBody>
  )
}
